<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row align-v="center" class="mt-3">
        <b-col>
          <h1>Onboarding Rappi</h1>
          <p class="text-muted mb-0">
            Conecta tu cuenta de Rappi y activa nuevas tiendas usando el flujo self-onboarding.
          </p>
        </b-col>
        <b-col cols="auto">
          <base-button type="secondary" size="sm" @click="refreshAll" :disabled="loading">
            <b-icon-arrow-clockwise></b-icon-arrow-clockwise> Refrescar
          </base-button>
        </b-col>
      </b-row>

      <!-- Step 1: connection / OAuth status -->
      <b-card class="mt-3">
        <h4>1. Conexión OAuth con Rappi</h4>
        <b-row align-v="center">
          <b-col>
            <p class="mb-1">
              <strong>Estado:</strong>
              <b-badge :variant="status.connected ? 'success' : 'warning'">
                {{ status.connected ? 'Conectado' : 'No conectado' }}
              </b-badge>
              <span v-if="status.connected && status.token_expires_at" class="ml-2 text-muted">
                Expira: {{ status.token_expires_at }}
              </span>
            </p>
            <p class="mb-1">
              <strong>Webhook configurado:</strong>
              <b-badge :variant="status.webhook_configured ? 'success' : 'secondary'">
                {{ status.webhook_configured ? 'Sí' : 'No' }}
              </b-badge>
            </p>
            <p class="mb-1 text-muted small">
              <strong>redirect_uri configurada:</strong>
              <code>{{ status.oauth_redirect_uri || '(no configurado, configura RAPPI_OAUTH_REDIRECT_URI en el backend o company.rappi_oauth_redirect_uri)' }}</code>
            </p>
          </b-col>
          <b-col cols="auto">
            <base-button
              v-if="!status.connected"
              type="success"
              size="sm"
              :disabled="loading || !status.rappi_integrator_ready"
              @click="startOAuth"
            >Conectar con Rappi</base-button>
            <base-button
              v-else
              type="warning"
              size="sm"
              :disabled="loading"
              @click="startOAuth"
            >Reconectar</base-button>
            <base-button
              v-if="status.connected"
              type="danger"
              size="sm"
              class="ml-2"
              :disabled="loading"
              @click="confirmDisconnect"
            >Desconectar</base-button>
          </b-col>
        </b-row>
        <b-alert :show="!status.rappi_integrator_ready" variant="danger" class="mt-3 mb-0">
          La compañía no tiene credenciales de integrador Rappi configuradas
          (<code>rappi_client_id</code>/<code>rappi_client_secret</code>). Pide al TAM
          de Rappi que cree el Integration entity y carga las credenciales.
        </b-alert>
      </b-card>

      <!-- Step 2: webhook setup -->
      <b-card class="mt-3" v-if="status.connected || status.rappi_integrator_ready">
        <h4>2. Webhook STORE_PROVISIONING_STATUS</h4>
        <p class="text-muted small">
          Rappi envía el resultado del provisioning de forma asincrónica a este
          endpoint. Configúralo una sola vez.
        </p>
        <b-form-group label="URL del webhook (opcional, default = SERVER_LINK + /rappi/webhooks/storeprovisioning)">
          <b-form-input v-model="webhookForm.url" placeholder="https://...back.../rappi/webhooks/storeprovisioning"></b-form-input>
        </b-form-group>
        <b-form-group label="Secret HMAC (opcional, se genera uno aleatorio si lo dejas vacío)">
          <b-form-input v-model="webhookForm.secret"></b-form-input>
        </b-form-group>
        <base-button type="primary" size="sm" :disabled="loading" @click="setupWebhook">
          {{ status.webhook_configured ? 'Re-registrar webhook' : 'Registrar webhook' }}
        </base-button>
      </b-card>

      <!-- Step 3: fetch and provision -->
      <b-card class="mt-3" v-if="status.connected">
        <b-row align-v="center">
          <b-col>
            <h4 class="mb-0">3. Tiendas disponibles en Rappi</h4>
            <p class="text-muted small mb-0">
              Selecciona las tiendas a activar y asigna cada una a una sucursal local.
            </p>
          </b-col>
          <b-col cols="auto">
            <base-button type="primary" size="sm" :disabled="loading" @click="fetchIntegrationStatus">
              Cargar tiendas
            </base-button>
          </b-col>
        </b-row>

        <b-table
          v-if="flatStores.length > 0"
          :items="flatStores"
          :fields="storeFields"
          striped
          small
          responsive="md"
          class="mt-3"
        >
          <template #cell(select)="data">
            <b-form-checkbox
              v-if="!data.item.integrated"
              v-model="data.item._selected"
            ></b-form-checkbox>
            <span v-else class="text-muted small">ya activa</span>
          </template>
          <template #cell(name)="data">
            <span v-if="data.item._depth > 0" class="text-muted">{{ '— '.repeat(data.item._depth) }}</span>
            {{ data.item.name }}
          </template>
          <template #cell(integrated)="data">
            <b-badge :variant="data.item.integrated ? 'success' : 'secondary'">
              {{ data.item.integrated ? 'sí' : 'no' }}
            </b-badge>
          </template>
          <template #cell(sucursal)="data">
            <b-form-select
              v-if="!data.item.integrated"
              v-model="data.item._sucursal_id"
              size="sm"
            >
              <template #first>
                <b-form-select-option :value="null">-- no enlazar --</b-form-select-option>
              </template>
              <b-form-select-option v-for="s in sucursales" :key="s.id" :value="s.id">
                {{ s.nombre }} ({{ s.code }})
              </b-form-select-option>
            </b-form-select>
          </template>
        </b-table>

        <b-alert v-if="loadedStores && flatStores.length === 0" show variant="info" class="mt-3">
          Rappi no devolvió tiendas para esta cuenta de comercio.
        </b-alert>

        <div v-if="flatStores.length > 0" class="mt-3">
          <base-button
            type="success"
            size="sm"
            :disabled="loading || selectedCount === 0"
            @click="provisionSelected"
          >
            Activar {{ selectedCount }} tienda(s)
          </base-button>
          <base-button
            type="danger"
            size="sm"
            class="ml-2"
            :disabled="loading || deprovisionableCount === 0"
            @click="deprovisionActive"
          >
            Desactivar las marcadas
          </base-button>
        </div>
      </b-card>

      <!-- Step 4: history -->
      <b-card class="mt-3">
        <b-row align-v="center">
          <b-col>
            <h4 class="mb-0">4. Historial de eventos</h4>
            <p class="text-muted small mb-0">
              Resultados confirmados por Rappi vía webhook + requests enviados desde el dashboard.
            </p>
          </b-col>
          <b-col cols="auto">
            <base-button type="secondary" size="sm" :disabled="loading" @click="fetchEvents">
              Recargar
            </base-button>
          </b-col>
        </b-row>
        <b-table
          v-if="events.length > 0"
          :items="events"
          :fields="eventFields"
          striped
          small
          responsive="md"
          class="mt-3"
        ></b-table>
        <p v-else class="text-muted small mb-0 mt-3">Sin eventos todavía.</p>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import axios from "axios";

export default {
  name: "RappiOnboarding",
  components: { Nav },
  data() {
    return {
      loading: false,
      status: {
        connected: false,
        token_expires_at: "",
        webhook_configured: false,
        oauth_redirect_uri: "",
        rappi_integrator_ready: false,
      },
      webhookForm: { url: "", secret: "" },
      sucursales: [],
      flatStores: [],
      loadedStores: false,
      events: [],
      storeFields: [
        { key: "select", label: "" },
        { key: "name", label: "Nombre" },
        { key: "brand", label: "Marca" },
        { key: "store_id", label: "store_id" },
        { key: "integration_id", label: "integration_id" },
        { key: "integrated", label: "Integrada" },
        { key: "sucursal", label: "Sucursal local" },
      ],
      eventFields: [
        { key: "created_at", label: "Cuándo" },
        { key: "operation", label: "Operación" },
        { key: "store_id", label: "store_id" },
        { key: "integration_id", label: "integration_id" },
        { key: "status", label: "Estado" },
        { key: "http_code", label: "HTTP" },
        { key: "message", label: "Mensaje" },
        { key: "batch_id", label: "batch" },
      ],
    };
  },
  created() {
    this.handleOAuthReturn();
    this.refreshAll();
  },
  computed: {
    selectedCount() {
      return this.flatStores.filter((s) => s._selected && !s.integrated).length;
    },
    deprovisionableCount() {
      return this.flatStores.filter((s) => s._selected && s.integrated).length;
    },
  },
  methods: {
    handleOAuthReturn() {
      const q = this.$route.query;
      if (q.rappi_oauth_success) {
        this.makeToast("Conectado", "Rappi autorizó la conexión", "success");
        this.$router.replace({ query: {} });
      } else if (q.rappi_oauth_error) {
        this.makeToast("Error OAuth", q.rappi_oauth_error, "danger");
        this.$router.replace({ query: {} });
      }
    },
    refreshAll() {
      this.loadStatus();
      this.loadSucursales();
      this.fetchEvents();
    },
    loadStatus() {
      this.loading = true;
      axios
        .get("/rappi/onboarding/status")
        .then((r) => {
          this.status = r.data;
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    loadSucursales() {
      axios
        .get("/sucursals")
        .then((r) => {
          this.sucursales = r.data.sort((a, b) => a.nombre.localeCompare(b.nombre));
        })
        .catch((err) => this.handleErr(err));
    },
    startOAuth() {
      const returnURL =
        window.location.origin + this.$router.resolve({ name: "RappiOnboarding" }).href;
      this.loading = true;
      axios
        .get("/rappi/onboarding/oauth/start", { params: { return_url: returnURL } })
        .then((r) => {
          if (r.data && r.data.authorize_url) {
            window.location.href = r.data.authorize_url;
          } else {
            this.makeToast("Error", "El backend no devolvió authorize_url", "danger");
          }
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    confirmDisconnect() {
      this.$bvModal
        .msgBoxConfirm(
          "Esto borra el merchant token. Tendrás que volver a autorizar para usar provisioning. ¿Continuar?",
          { title: "Desconectar Rappi", okVariant: "danger", okTitle: "Desconectar", cancelTitle: "Cancelar", centered: true }
        )
        .then((ok) => {
          if (ok) this.disconnect();
        });
    },
    disconnect() {
      this.loading = true;
      axios
        .post("/rappi/onboarding/oauth/disconnect")
        .then(() => {
          this.makeToast("Desconectado", "Token de comercio eliminado", "success");
          this.loadStatus();
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    setupWebhook() {
      this.loading = true;
      axios
        .post("/rappi/onboarding/webhook", {
          url: this.webhookForm.url || undefined,
          secret: this.webhookForm.secret || undefined,
        })
        .then((r) => {
          this.makeToast(
            "Webhook registrado",
            "URL: " + (r.data && r.data.url ? r.data.url : "(default)"),
            "success"
          );
          this.loadStatus();
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    fetchIntegrationStatus() {
      this.loading = true;
      axios
        .get("/rappi/onboarding/integration-status")
        .then((r) => {
          const stores = (r.data && r.data.stores) || r.data || [];
          this.flatStores = this.flattenStores(stores, 0);
          this.loadedStores = true;
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    flattenStores(nodes, depth) {
      const out = [];
      (nodes || []).forEach((n) => {
        out.push({
          store_id: n.store_id || "",
          integration_id: n.integration_id || "",
          name: n.name || "",
          brand: n.brand || "",
          integrated: !!n.integrated,
          status: n.status || "",
          _depth: depth,
          _selected: false,
          _sucursal_id: null,
        });
        if (n.children && n.children.length) {
          out.push(...this.flattenStores(n.children, depth + 1));
        }
      });
      return out;
    },
    provisionSelected() {
      const stores = this.flatStores
        .filter((s) => s._selected && !s.integrated)
        .map((s) => ({
          store_id: s.store_id,
          integration_id: s.integration_id,
          name: s.name,
          status: "ACTIVE",
          sucursal_id: s._sucursal_id || 0,
        }));
      if (stores.length === 0) return;
      if (stores.length > 20) {
        this.makeToast("Error", "Máximo 20 tiendas por request", "danger");
        return;
      }
      this.loading = true;
      axios
        .post("/rappi/onboarding/provisioning", { stores })
        .then((r) => {
          this.makeToast(
            "Solicitado",
            "Rappi confirmará por webhook. Batch: " + (r.data && r.data.batch_id ? r.data.batch_id : ""),
            "success"
          );
          this.fetchEvents();
          this.fetchIntegrationStatus();
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    deprovisionActive() {
      const stores = this.flatStores
        .filter((s) => s._selected && s.integrated)
        .map((s) => ({ store_id: s.store_id, integration_id: s.integration_id }));
      if (stores.length === 0) return;
      this.$bvModal
        .msgBoxConfirm(
          `Esto desactivará ${stores.length} tienda(s) en Rappi. ¿Continuar?`,
          { title: "Desactivar tiendas", okVariant: "danger", okTitle: "Desactivar", cancelTitle: "Cancelar", centered: true }
        )
        .then((ok) => {
          if (!ok) return;
          this.loading = true;
          axios
            .post("/rappi/onboarding/deprovisioning", { stores })
            .then((r) => {
              this.makeToast(
                "Solicitado",
                "Batch: " + (r.data && r.data.batch_id ? r.data.batch_id : ""),
                "success"
              );
              this.fetchEvents();
              this.fetchIntegrationStatus();
            })
            .catch((err) => this.handleErr(err))
            .finally(() => (this.loading = false));
        });
    },
    fetchEvents() {
      axios
        .get("/rappi/onboarding/events", { params: { limit: 50 } })
        .then((r) => {
          this.events = r.data || [];
        })
        .catch((err) => this.handleErr(err));
    },
    handleErr(err) {
      const msg = err.request && err.request.response ? err.request.response : err.message;
      this.makeToast("Error", msg, "danger");
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    },
  },
};
</script>
